import api from './api'
import { socket } from '@/plugins/socket'
const moment = require('moment')

const BATCH_SIZE = 1;
const WATCH_TIMEOUT_MS = 5 * 60 * 1000; // 5 минут
const POLL_INTERVAL_MS = 3000;
const COMMIT_RETRYABLE_HTTP_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function isCommitRetriableError(error) {
    if (!error) return false;
    const response = error.response;
    if (!response) return true;
    const status = response.status;
    return COMMIT_RETRYABLE_HTTP_STATUSES.has(status) || status >= 500;
}

export default {
    actions: {
        shiftReport_init({ dispatch, commit }) {
            dispatch('shiftReport_loadSubmissionSettings').catch(() => {});
            dispatch('shiftReport_getObjects')
                .then(objects => commit('shiftReport_setAvailableObjects', objects));
            dispatch('shiftReport_setDefaultTime');
        },
        async shiftReport_loadSubmissionSettings({commit, rootGetters}) {
            const company = rootGetters.currentUser.company;
            try {
                const settings = await api.getSubmissionSettings();
                if (rootGetters.currentUser.company !== company) throw new Error('Компания изменилась. Откройте отчёт заново.');
                commit('shiftReport_setSubmissionSettings', {enabled: settings.text_quality_enabled === true, error: ''});
                return settings;
            } catch (error) {
                if (rootGetters.currentUser.company === company) commit('shiftReport_setSubmissionSettings', {
                    error: 'Не удалось загрузить правила заполнения. Повторите загрузку перед отправкой.'
                });
                throw error;
            }
        },
        shiftReport_getObjects() {
            return api.getObjects();
        },
        shiftReport_getTargets({ state, commit }) {
            return api.getTargets(state.selectedProcess)
                .then(targets => commit('shiftReport_setTargets', targets));
        },
        shiftReport_getFuelLimits({ state }) {
            return api.getFuelLimits(state.selectedProcess);
        },
        shiftReport_getMaterials(_, { row_id }) {
            return api.getMaterials(row_id);
        },
        async shiftReport_createCache({ dispatch }) {
            const payload = await dispatch('getReportPayload');
            return api.createCache(payload);
        },     
        getReportPayload({ state }, photosFromBackend = []) {
            const {
                selectedProcess,
                startDate,
                startTime,
                endDate,
                endTime,
                peopleCount,
                pauseTime,
                selectedWorks,
                secondWorks,
                notes,
                fuel,              
                selectedFuelIds,
                emptyPhotoComment,
                reasonForDelay
            } = state;

            const selectedSet = new Set(Array.isArray(selectedFuelIds) ? selectedFuelIds : []);

            const fuelPayload = (Array.isArray(fuel) ? fuel : [])
                .filter(f => selectedSet.has(f.id))
                .map(f => ({
                    id: f.id,
                    name: f.name,
                    unit: f.unit,
                    value: Number(f.value) || 0,
                    note: f.note || ''
                }));

            // Фильтруем материалы: оставляем только те, где used явно заполнено
            const filteredSelectedWorks = (Array.isArray(selectedWorks) ? selectedWorks : []).map(work => {
                const filteredMaterials = (Array.isArray(work.materials) ? work.materials : [])
                    .filter(material => {
                        const used = material.used;
                        return used !== null && used !== '' && used !== undefined;
                    });
                
                return {
                    ...work,
                    materials: filteredMaterials
                };
            });

            return {
                processId: selectedProcess,
                startDate,
                report: {
                    startDate,
                    startTime,
                    endDate,
                    endTime,
                    peopleCount: peopleCount || 0,
                    pauseTime: pauseTime || 0,
                    selectedWorks: filteredSelectedWorks,
                    secondWorks,
                    notes,
                    fuel: fuelPayload,
                    emptyPhotoComment,
                    delayReasonKey: state.delayReasonKey,
                    photoReasonKey: state.photoReasonKey,
                    photosNotAvailable: state.photosNotAvailable,
                    photos: photosFromBackend,
                    reusedPhotos: state.reusedPhotos,
                    reusedPhotoSelectionInitialized: state.reusedPhotoSelectionInitialized,
                    reasonForDelay
                }
            }
        },

        shiftReport_getAvailableCache({ dispatch, commit }) {
            commit('shiftReport_setAvailableCache', []);
            return dispatch('shiftReport_createCache').then(result => {
                if (result.created) {
                    commit('shiftReport_setCache', result.row);
                } else {
                    commit('shiftReport_setAvailableCache', [result.row]);
                }
                return result;
            });
        },

        async shiftReport_updateCache({ state, dispatch }) {
            const { cache, startDate } = state;
            if (!cache || (cache.report.startDate !== startDate)) {
                return;
            }
            const payload = await dispatch('getReportPayload');
            return api.updateCache(cache.id, payload);
        },
      
        shiftReport_uploadCache({ state, commit }) {
            const { availableCache } = state;
            const {
                startDate,
                startTime,
                endDate,
                endTime,
                peopleCount,
                pauseTime,
                selectedWorks,
                secondWorks,
                notes,
                fuel: cachedFuel,
                reasonForDelay
            } = availableCache[0].report;

            commit('shiftReport_setStartDate', startDate);
            commit('shiftReport_setStartTime', startTime);
            commit('shiftReport_setEndDate', endDate);
            commit('shiftReport_setEndTime', endTime);
            commit('shiftReport_setPeopleCount', peopleCount);
            commit('shiftReport_setPauseTime', pauseTime);
            commit('shiftReport_setSelectedWorks', selectedWorks);
            commit('shiftReport_setSecondWorks', secondWorks);
            commit('shiftReport_setNotes', notes);
            commit('shiftReport_setReasonForDelay', reasonForDelay);
            const cachedReport = availableCache[0].report;
            commit('shiftReport_setReusedPhotos', cachedReport.reusedPhotos || []);
            commit('shiftReport_setReusedPhotoSelectionInitialized', cachedReport.reusedPhotoSelectionInitialized === true || Boolean(cachedReport.reusedPhotos && cachedReport.reusedPhotos.length));
            commit('shiftReport_setDelayReasonKey', cachedReport.delayReasonKey || '');
            commit('shiftReport_setPhotoReasonKey', cachedReport.photoReasonKey || '');
            commit('shiftReport_setEmptyPhotoComment', cachedReport.emptyPhotoComment || '');
            commit('shiftReport_setPhotosNotAvailable', cachedReport.photosNotAvailable === true || Boolean(cachedReport.emptyPhotoComment));

            // Восстанавливаем выбор чекбоксов
            const ids = Array.isArray(cachedFuel) ? cachedFuel.map(f => f.id) : [];
            commit('shiftReport_setSelectedFuelIds', ids);

            // Мерджим значения обратно в каталог топлива, не теряя остальные позиции
            const currentFuel = Array.isArray(state.fuel) ? state.fuel.slice() : [];
            const byId = new Map(currentFuel.map(f => [f.id, { ...f }]));

            (Array.isArray(cachedFuel) ? cachedFuel : []).forEach(cf => {
                const prev = byId.get(cf.id) || { id: cf.id, name: cf.name, unit: cf.unit };
                byId.set(cf.id, {
                    ...prev,
                    // сохраняем сырое значение, чтобы инпут мог показывать '' (если надо)
                    value: (cf.value === '' || cf.value == null) ? '' : cf.value,
                    note: cf.note || ''
                });
            });

            commit('shiftReport_setFuel', Array.from(byId.values()));

            commit('shiftReport_setAvailableCache', []);
            commit('shiftReport_setCache', availableCache[0]);
        },

        async shiftReport_loadReusablePhotos({state, commit, rootGetters}) {
            const processId = state.selectedProcess, date = state.startDate;
            if (!processId || !date) return;
            const identity = () => { const user = rootGetters.currentUser || {}; return `${user.company}:${user.userId || user.id}`; };
            const key = `${identity()}:${processId}:${date}`;
            const request = (state.reusablePhotoLookup.request || 0) + 1;
            if (state.reusablePhotoLookup.identity && state.reusablePhotoLookup.identity !== identity()) {
                commit('shiftReport_setReusedPhotos', []);
                commit('shiftReport_setReusedPhotoSelectionInitialized', false);
            }
            commit('shiftReport_setReusablePhotoLookup', {key, request, identity: identity(), status: 'loading', error: ''});
            try {
                const result = await api.getReusablePhotos({processId, date});
                if (`${identity()}:${state.selectedProcess}:${state.startDate}` !== key || state.reusablePhotoLookup.request !== request) return;
                const items = result.items || [];
                const ids = new Set((state.reusedPhotos || []).map(item => Number(item.id)));
                const selectByDefault = !state.reusedPhotoSelectionInitialized && !ids.size;
                const selected = items.filter(item => item.selectable && (selectByDefault || ids.has(Number(item.id))));
                commit('shiftReport_setReusablePhotoLookup', {key, status: 'ready', items,
                    error: selected.length < ids.size ? 'Часть выбранных фото больше недоступна. Проверьте состав перед подачей.' : ''});
                commit('shiftReport_setReusedPhotos', selected);
            } catch (error) {
                if (`${identity()}:${state.selectedProcess}:${state.startDate}` !== key || state.reusablePhotoLookup.request !== request) return;
                commit('shiftReport_setReusablePhotoLookup', {key, status: 'error',
                    error: error.response && error.response.data && error.response.data.msg || 'Не удалось подобрать фото. Повторите попытку.'});
            }
        },

        async shiftReport_uploadTempPhoto(_, file) {
            const maxAttempts = 3;
            const baseDelay = 500;

            for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                try {
                    return await api.uploadTempPhoto(file);
                } catch (error) {
                    if (attempt === maxAttempts) {
                        const wrapped = new Error(error && error.message ? error.message : 'Не удалось загрузить файл');
                        wrapped.code = 'TEMP_UPLOAD_FAILED';
                        throw wrapped;
                    }
                    const delay = baseDelay * Math.pow(2, attempt - 1);
                    await new Promise(resolve => setTimeout(resolve, delay));
                }
            }
        },

        /**
         * Удаляет временные фото (Media.status = 'temp') по списку id.
         * Используется при поштучном удалении и при "Очистить всё" в PhotoUploadDevice.
         */
        async shiftReport_deleteTempPhotos(_, ids) {
            const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
            if (!list.length) {
                return;
            }
            try {
                await api.deleteTempPhotos(list);
            } catch (error) {
                // Ошибку гасим: устаревшие temp-файлы всё равно удалит cleanupTempMedia
                // Можно добавить логирование в будущем, если понадобится
            }
        },

        async shiftReport_attachTempPhotos({ commit }, { shiftId, photos }) {
            const list = Array.isArray(photos) ? photos : [];
            const tempIds = list.map(p => p.id).filter(Boolean);
            if (!shiftId || !tempIds.length) {
                return;
            }

            const total = tempIds.length;
            commit('shiftReport_setUploadProgress', { done: 0, total });

            try {
                let remainingIds = Array.from(new Set(tempIds));
                let attachedIds = [];

                const commitWithRetry = async (ids) => {
                    const maxAttempts = 3;
                    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                        try {
                            return await api.commitTempPhotos({ shiftId, tempIds: ids });
                        } catch (error) {
                            if (attempt >= maxAttempts || !isCommitRetriableError(error)) {
                                throw error;
                            }
                            await sleep(400 * Math.pow(2, attempt - 1));
                        }
                    }
                    return null;
                };

                for (let pass = 1; pass <= 3 && remainingIds.length; pass += 1) {
                    const response = await commitWithRetry(remainingIds);
                    const attached = Array.isArray(response && response.ids) ? response.ids : [];
                    const failed = Array.isArray(response && response.failedIds) ? response.failedIds : [];

                    attached.forEach(id => attachedIds.push(id));
                    attachedIds = Array.from(new Set(attachedIds));
                    remainingIds = Array.from(new Set(failed));

                    commit('shiftReport_setUploadProgress', {
                        done: Math.min(total, attachedIds.length),
                        total
                    });
                }

                if (remainingIds.length) {
                    const wrappedPartial = new Error(
                        `Часть фотографий не удалось привязать автоматически: ${remainingIds.length} из ${total}`
                    );
                    wrappedPartial.code = 'PHOTO_ATTACH_PARTIAL';
                    wrappedPartial.payload = {
                        total,
                        attachedIds,
                        failedIds: remainingIds
                    };
                    throw wrappedPartial;
                }

                commit('shiftReport_setUploadProgress', { done: total, total });
            } catch (error) {
                const wrapped = new Error(error && error.message ? error.message : 'Ошибка при привязке фотографий');
                wrapped.code = 'PHOTO_ATTACH_FAILED';
                wrapped.payload = error && error.payload ? error.payload : null;
                throw wrapped;
            }
        },

        async shiftReport_sendReport({ state, dispatch, commit }) {
            let shift;
            try {
                await dispatch('shiftReport_loadSubmissionSettings');
                const payload = await dispatch('getReportPayload');
                const tempIds = state.uploadMethod === 'uploadByDevice'
                    ? (Array.isArray(state.photos) ? state.photos.map(p => p.id).filter(Boolean) : [])
                    : [];
                payload.serverProcessing = true;
                payload.clientFlow = {
                    uploadMethod: state.uploadMethod,
                    tempIds,
                    reusedMediaIds: state.uploadMethod === 'uploadByDevice' ? state.reusedPhotos.map(photo => photo.id) : []
                };
                ({ shift } = await api.sendReport(payload));
            } catch (error) {
                // Специальная обработка конфликта: по этой смене уже есть неотправленный отчёт
                const response = error && error.response;
                if (response && response.status === 409 && response.data && response.data.status === 'duplicate') {
                    const serverMsg = response.data && (response.data.msg || response.data.message);
                    const wrapped = new Error(serverMsg || 'По этой смене уже есть незавершённый отчёт.');
                    wrapped.code = 'REPORT_EXISTS';
                    wrapped.payload = response.data;
                    throw wrapped;
                }

                throw error;
            }

            const { photos, uploadMethod } = state;
            const acceptedPhotoCount = (Array.isArray(photos) ? photos.length : 0) + state.reusedPhotos.length;

            // Сбрасываем статус загрузки фотографий перед новой отправкой
            commit('shiftReport_setPhotosUploadStatus', null);
            commit('shiftReport_setPhotosUploadErrorMessage', null);
            commit('shiftReport_setUploadProgress', {
                done: uploadMethod === 'uploadByDevice' ? acceptedPhotoCount : 0,
                total: uploadMethod === 'uploadByDevice' ? acceptedPhotoCount : 0
            });
            commit('shiftReport_setPhotosUploadStatus', 'ok');

            return shift;
        },

        shiftReport_clearForm({ commit }) {
            commit('shiftReport_setReusedPhotos', []);
            commit('shiftReport_setReusedPhotoSelectionInitialized', false);
            commit('shiftReport_setReusablePhotoLookup', {key: '', status: 'idle', items: [], error: ''});
            commit('shiftReport_setDelayReasonKey', '');
            commit('shiftReport_setPhotoReasonKey', '');
            commit('shiftReport_setPeopleCount', null);
            commit('shiftReport_setPauseTime', null);
            commit('shiftReport_setAvailableCache', []);
            commit('shiftReport_setCache', null);
            commit('shiftReport_setSelectedWorks', []);
            commit('shiftReport_setSecondWorks', null);
            commit('shiftReport_setNotes', null);
            commit('shiftReport_setPhotos', []);
            commit('shiftReport_setPhotosNotAvailable', false);
            commit('shiftReport_setEmptyPhotoComment', null);
            commit('shiftReport_setSelectedFuelIds', []);
            commit('shiftReport_setReasonForDelay', null);
        },

        shiftReport_finishReportNotification(_, shiftId) {
            return api.finishReportNotification(shiftId);
        },
        shiftReport_sendPhotos(_, shiftId) {
            return api.sendPhotos(shiftId);
        },
        shiftReport_sendReasonForDelay(_, shiftId) {
            return api.sendReasonForDelay(shiftId);
        },
        shiftReport_resendProcessedPhotos(_, shiftId) {
            return api.resendProcessedPhotos(shiftId);
        },

        shiftReport_setDefaultTime({ commit }) {
            const start = moment();
            start.set('hour', 8);
            start.set('minute', 30);
            commit('shiftReport_setStartTime', start.format('HH:mm'));
            const end = moment();
            end.set('hour', 17);
            end.set('minute', 0);
            commit('shiftReport_setEndDate', end.format('YYYY-MM-DD'));
            commit('shiftReport_setEndTime', end.format('HH:mm'));
        },

        shiftReport_sendTriggerToSendPhotoFromChat(_, { shiftId, reasonForDelay }) {
            return api.sendTriggerToSendPhotoFromChat({ shiftId, reasonForDelay });
        },

        /**
         * Наблюдает за фоновой обработкой фотографий.
         * Стратегия:
         * 1. Подключается к socket.io комнате shift:<shiftId>, слушает photos:completed
         * 2. При ошибке подключения — fallback на polling GET /photos/status каждые 3с
         * 3. Таймаут 5 минут
         * Возвращает функцию stop() для отмены из beforeDestroy.
         */
        shiftReport_watchProgress({ commit, rootState }, shiftId) {
            const abortTokenAtStart = rootState && rootState.lifecycle && typeof rootState.lifecycle.abortToken !== 'undefined'
                ? rootState.lifecycle.abortToken
                : 0;

            commit('shiftReport_setPhotosProcessingStatus', 'processing');
            const deadline = Date.now() + WATCH_TIMEOUT_MS;
            let pollingTimer = null;
            let socketConnected = false;

            const isAborted = () => {
                const current = rootState && rootState.lifecycle && typeof rootState.lifecycle.abortToken !== 'undefined'
                    ? rootState.lifecycle.abortToken
                    : abortTokenAtStart;
                return current !== abortTokenAtStart;
            };

            function stopPolling() {
                if (pollingTimer) {
                    clearInterval(pollingTimer);
                    pollingTimer = null;
                }
            }

            function onCompleted() {
                // После lifecycle-abort не меняем UI-статусы.
                // Наблюдение может продолжаться best-effort, но коммиты должны быть пропущены.
                if (isAborted()) {
                    stopPolling();
                    if (socketConnected) {
                        socket.emit('leave', `shift:${shiftId}`);
                        socket.disconnect();
                        socketConnected = false;
                    }
                    return;
                }
                stopPolling();
                commit('shiftReport_setPhotosProcessingStatus', 'completed');
                if (socketConnected) {
                    socket.emit('leave', `shift:${shiftId}`);
                    socket.disconnect();
                    socketConnected = false;
                }
            }

            function onError() {
                if (isAborted()) {
                    stopPolling();
                    if (socketConnected) {
                        socket.emit('leave', `shift:${shiftId}`);
                        socket.disconnect();
                        socketConnected = false;
                    }
                    return;
                }
                stopPolling();
                commit('shiftReport_setPhotosProcessingStatus', 'error');
                if (socketConnected) {
                    socket.emit('leave', `shift:${shiftId}`);
                    socket.disconnect();
                    socketConnected = false;
                }
            }

            function startPolling() {
                pollingTimer = setInterval(async () => {
                    if (Date.now() > deadline) {
                        stopPolling();
                        if (!isAborted()) {
                            commit('shiftReport_setPhotosProcessingStatus', 'timeout');
                        }
                        return;
                    }
                    try {
                        const counts = await api.getPhotosStatus(shiftId);
                        const hasPending = counts.some(c => ['pending', 'processed', 'temp', 'reuse_pending'].includes(c.status));
                        if (!hasPending && counts.length > 0 && !isAborted()) {
                            onCompleted();
                        }
                    } catch (e) {
                        // Сеть недоступна — продолжаем попытки
                    }
                }, POLL_INTERVAL_MS);
            }

            try {
                socket.connect();
                socket.emit('join', `shift:${shiftId}`);
                socketConnected = true;

                socket.once('photos:completed', onCompleted);
                socket.once('photos:error', onError);

                socket.once('connect_error', () => {
                    socketConnected = false;
                    socket.off('photos:completed', onCompleted);
                    socket.off('photos:error', onError);
                    startPolling();
                });
            } catch (e) {
                startPolling();
            }

            // Возвращаем функцию отмены для вызова из beforeDestroy
            return function stop() {
                stopPolling();
                socket.off('photos:completed', onCompleted);
                socket.off('photos:error', onError);
                if (socketConnected) {
                    socket.emit('leave', `shift:${shiftId}`);
                    socket.disconnect();
                    socketConnected = false;
                }
            };
        }
    },

    getters: {
        // Проверяет что ядро формы заполнено: объект, топливо, люди, работы, баланс.
        // Используется lifecycle для принятия решения о пропуске destructive abort.
        shiftReport_isCoreFormReady(state) {
            if (!state.selectedProcess) return false;
            if (!Array.isArray(state.fuel) || state.fuel.length === 0) return false;
            if (!state.peopleCount || Number(state.peopleCount) <= 0) return false;
            if (state.pauseTime == null || Number(state.pauseTime) < 0) return false;
            if (!Array.isArray(state.selectedWorks) || state.selectedWorks.length === 0) return false;
            const worksPeopleSum = state.selectedWorks.reduce(
                (acc, w) => acc + (Number(w.peopleCount) || 0),
                0
            );
            if (worksPeopleSum !== Number(state.peopleCount)) return false;
            return true;
        },
    },

    state: {
        availableObjects: [],
        selectedProcess: null,
        startDate: moment().format('YYYY-MM-DD'),
        startTime: new Date(),
        endDate: new Date(),
        endTime: new Date(),
        peopleCount: null,
        pauseTime: null,
        targets: [],
        cache: null,
        availableCache: [],
        selectedWorks: [],
        secondWorks: null,
        notes: null,
        fuel: [],
        selectedFuelIds: [],
        photos: [],
        reusedPhotos: [],
        reusedPhotoSelectionInitialized: false,
        reusablePhotoLookup: {key: '', status: 'idle', items: [], error: ''},
        photosNotAvailable: false,
        textQualityEnabled: false,
        submissionSettingsError: '',
        delayReasonKey: '',
        photoReasonKey: '',
        emptyPhotoComment: ``,
        onProcess: false,
        reasonForDelay: null,
        uploadMethod: 'uploadByDevice',
        uploadProgress: { done: 0, total: 0 },
        photosProcessingStatus: null, // null | 'processing' | 'completed' | 'error' | 'timeout'
        photosUploadStatus: null, // null | 'ok' | 'failed' | 'partial'
        photosUploadErrorMessage: null,
        criticalSubmissionActive: false
    },

    mutations: {
        shiftReport_setReusedPhotoSelectionInitialized(state, value) { state.reusedPhotoSelectionInitialized = value; },
        shiftReport_setReusedPhotos(state, value) {
            state.reusedPhotos = Array.isArray(value) ? value : [];
            state.reusedPhotoSelectionInitialized = true;
            if (state.reusedPhotos.length) state.photosNotAvailable = false;
        },
        shiftReport_setReusablePhotoLookup(state, value) {
            state.reusablePhotoLookup = {...state.reusablePhotoLookup, ...value};
        },
        shiftReport_setSubmissionSettings(state, payload) {
            if (payload.enabled !== undefined) state.textQualityEnabled = payload.enabled;
            state.submissionSettingsError = payload.error || '';
        },
        shiftReport_setDelayReasonKey(state, value) { state.delayReasonKey = value || ''; },
        shiftReport_setPhotoReasonKey(state, value) { state.photoReasonKey = value || ''; },
        shiftReport_setAvailableObjects(state, value) {
            state.availableObjects = value;
        },
        shiftReport_setSelectedProcess(state, value) {
            if (String(state.selectedProcess) !== String(value)) {
                state.reusedPhotos = [];
                state.reusedPhotoSelectionInitialized = false;
                state.reusablePhotoLookup = {key: '', request: (state.reusablePhotoLookup.request || 0) + 1, status: 'idle', items: [], error: ''};
            }
            state.selectedProcess = value;
        },
        shiftReport_setStartDate(state, value) {
            if (state.startDate !== value) {
                state.reusedPhotos = [];
                state.reusedPhotoSelectionInitialized = false;
                state.reusablePhotoLookup = {key: '', request: (state.reusablePhotoLookup.request || 0) + 1, status: 'idle', items: [], error: ''};
            }
            state.startDate = value;
        },
        shiftReport_setStartTime(state, value) {
            state.startTime = value;
        },
        shiftReport_setEndDate(state, value) {
            state.endDate = value;
        },
        shiftReport_setEndTime(state, value) {
            state.endTime = value;
        },
        shiftReport_setPeopleCount(state, value) {
            state.peopleCount = value;
        },
        shiftReport_setPauseTime(state, value) {
            state.pauseTime = value;
        },
        shiftReport_setTargets(state, value) {
            state.targets = value;
        },
        shiftReport_setAvailableCache(state, value) {
            state.availableCache = value;
        },
        shiftReport_setCache(state, value) {
            state.cache = value;
        },
        shiftReport_setSelectedWorks(state, value) {
            state.selectedWorks = value;
        },
        shiftReport_setSecondWorks(state, value) {
            state.secondWorks = value;
        },
        shiftReport_setNotes(state, value) {
            state.notes = value;
        },
        shiftReport_setFuel(state, value) {
            state.fuel = value;
        },      
        shiftReport_setSelectedFuelIds(state, ids) {
            state.selectedFuelIds = Array.isArray(ids) ? ids : [];
        },
        shiftReport_setPhotos(state, value) {
            state.photos = value;
        },
        shiftReport_setPhotosNotAvailable(state, value) {
            state.photosNotAvailable = value;
        },
        shiftReport_setEmptyPhotoComment(state, value) {
            state.emptyPhotoComment = value;
        },
        shiftReport_setOnProcess(state, value) {
            state.onProcess = value;
        },
        shiftReport_setReasonForDelay(state, value) {
            state.reasonForDelay = value;
        },
        shiftReport_setUploadMethod(state, value) {
            state.uploadMethod = value;
        },
        shiftReport_setUploadProgress(state, value) {
            state.uploadProgress = value;
        },
        shiftReport_setPhotosProcessingStatus(state, value) {
            state.photosProcessingStatus = value;
        },
        shiftReport_setPhotosUploadStatus(state, value) {
            state.photosUploadStatus = value;
        },
        shiftReport_setPhotosUploadErrorMessage(state, value) {
            state.photosUploadErrorMessage = value;
        },
        shiftReport_setCriticalSubmissionActive(state, value) {
            state.criticalSubmissionActive = Boolean(value);
        }
    }
}
